// src/components/Admin/Cantieri/SettingsDropdownPortal.jsx
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const SettingsDropdownPortal = ({ 
  children, 
  top = 0, 
  left = 0, 
  isMobile = false,
  onClose = () => {}
}) => {
  const dropdownRef = useRef(null);
  // Debug della posizione ricevuta
  useEffect(() => {
    if (!isMobile) {
      console.log('🎯 SettingsDropdownPortal - Posizione ricevuta:', { top, left });
      console.log('🎯 Viewport:', { width: window.innerWidth, height: window.innerHeight });
    }
  }, [top, left, isMobile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleScroll = () => {
      // Chiudi il dropdown quando si fa scroll per evitare posizionamenti errati
      onClose();
    };

    const handleResize = () => {
      onClose();
    };

    // Aggiungi event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    document.addEventListener('scroll', handleScroll, true); // true per catturare tutti gli scroll
    window.addEventListener('resize', handleResize);

    // Su mobile, blocca lo scroll del body
    if (isMobile) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('touchstart', handleClickOutside);
        document.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
        document.body.style.overflow = originalOverflow;
      };
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [onClose, isMobile]);

  // 🎯 CALCOLO SMART DELLA POSIZIONE
  const calculateSmartPosition = () => {
    if (isMobile) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownWidth = 200;
    const dropdownHeight = 180; // Altezza stimata per 4 pulsanti

    let finalTop = top;
    let finalLeft = left;
    let transformOrigin = 'top left';

    // 🔧 CONTROLLO SPAZIO VERTICALE
    const spaceBelow = viewportHeight - top;
    const spaceAbove = top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      // Non c'è spazio sotto, ma c'è spazio sopra
      finalTop = top - dropdownHeight - 8; // 8px di margine
      transformOrigin = 'bottom left';
    }

    // 🔧 CONTROLLO SPAZIO ORIZZONTALE
    if (left + dropdownWidth > viewportWidth) {
      // Non c'è spazio a destra, allinea a destra del pulsante
      finalLeft = left - dropdownWidth + 32; // 32px = larghezza stimata del pulsante
      transformOrigin = transformOrigin.replace('left', 'right');
    }

    // 🔧 SICUREZZA: Non uscire mai dai bordi
    finalLeft = Math.max(8, Math.min(finalLeft, viewportWidth - dropdownWidth - 8));
    finalTop = Math.max(8, Math.min(finalTop, viewportHeight - dropdownHeight - 8));

    return {
      position: 'fixed',
      top: `${finalTop}px`,
      left: `${finalLeft}px`,
      transform: 'none',
      transformOrigin
    };
  };

  const dropdownStyle = {
    ...calculateSmartPosition(),
    zIndex: 2147483647,
    
    // Stili base
    background: '#ffffff',
    border: isMobile ? '3px solid #007bff' : '2px solid #e9ecef',
    borderRadius: isMobile ? '16px' : '8px',
    boxShadow: isMobile 
      ? '0 20px 60px rgba(0, 0, 0, 0.8)' 
      : '0 8px 25px rgba(0, 0, 0, 0.3)',
    
    // Dimensioni
    minWidth: isMobile ? '280px' : '200px',
    maxWidth: isMobile ? '320px' : '250px',
    width: isMobile ? '85%' : 'auto',
    
    // Animazione
    animation: isMobile 
      ? 'portalDropdownMobile 0.3s ease-out' 
      : 'portalDropdownDesktop 0.2s ease-out',
    
    // Overflow
    overflow: 'hidden',
    
    // Forza rendering
    isolation: 'isolate',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden'
  };

  const portalContent = (
    <>
      {/* Stili CSS inline per le animazioni */}
      <style>
        {`
          @keyframes portalDropdownMobile {
            0% {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.7);
              filter: blur(2px);
            }
            100% {
              opacity: 1;
              transform: translate(-50%, -50%) scale(1);
              filter: blur(0);
            }
          }
          
          @keyframes portalDropdownDesktop {
            0% {
              opacity: 0;
              transform: scale(0.9) translateY(-10px);
            }
            100% {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          
          .settings-dropdown-portal {
            /* Assicura che il dropdown sia sempre visibile */
            pointer-events: auto !important;
          }
          
          .portal-dropdown-item {
            display: flex !important;
            align-items: center !important;
            justify-content: flex-start !important;
            width: 100% !important;
            padding: ${isMobile ? '18px 20px' : '12px 16px'} !important;
            margin: 0 !important;
            border: none !important;
            background: #ffffff !important;
            color: #333333 !important;
            font-size: ${isMobile ? '1.1rem' : '0.9rem'} !important;
            font-weight: 500 !important;
            text-align: left !important;
            cursor: pointer !important;
            border-bottom: 1px solid #e9ecef !important;
            min-height: ${isMobile ? '60px' : '44px'} !important;
            line-height: 1.3 !important;
            transition: all 0.2s ease !important;
            touch-action: manipulation !important;
            pointer-events: auto !important;
            user-select: none !important;
            -webkit-user-select: none !important;
            -webkit-tap-highlight-color: transparent !important;
            box-sizing: border-box !important;
            gap: 12px !important;
          }
          
          .portal-dropdown-item:hover,
          .portal-dropdown-item:focus {
            background: #f8f9ff !important;
            color: #0056b3 !important;
            border-left: 4px solid #007bff !important;
            padding-left: ${isMobile ? '16px' : '12px'} !important;
            transform: translateX(2px) !important;
          }
          
          .portal-dropdown-item:active {
            background: #e3f2fd !important;
            color: #003d82 !important;
            transform: translateX(2px) scale(0.98) !important;
          }
          
          .portal-dropdown-item:first-child {
            border-radius: ${isMobile ? '13px 13px 0 0' : '6px 6px 0 0'} !important;
          }
          
          .portal-dropdown-item:last-child {
            border-radius: ${isMobile ? '0 0 13px 13px' : '0 0 6px 6px'} !important;
            border-bottom: none !important;
          }
          
          .portal-dropdown-item.info {
            color: #0c5460 !important;
          }
          
          .portal-dropdown-item.info:hover,
          .portal-dropdown-item.info:focus {
            background: #d1ecf1 !important;
            color: #0c5460 !important;
            border-left-color: #17a2b8 !important;
          }
          
          .portal-dropdown-item.warning {
            color: #856404 !important;
          }
          
          .portal-dropdown-item.warning:hover,
          .portal-dropdown-item.warning:focus {
            background: #fff3cd !important;
            color: #856404 !important;
            border-left-color: #ffc107 !important;
          }
          
          .portal-dropdown-item.success {
            color: #155724 !important;
          }
          
          .portal-dropdown-item.success:hover,
          .portal-dropdown-item.success:focus {
            background: #d4edda !important;
            color: #155724 !important;
            border-left-color: #28a745 !important;
          }
          
          .portal-dropdown-item.danger {
            color: #721c24 !important;
          }
          
          .portal-dropdown-item.danger:hover,
          .portal-dropdown-item.danger:focus {
            background: #f8d7da !important;
            color: #721c24 !important;
            border-left-color: #dc3545 !important;
          }
          
          .portal-dropdown-header {
            display: ${isMobile ? 'block' : 'none'} !important;
            background: linear-gradient(135deg, #007bff, #0056b3) !important;
            color: white !important;
            padding: 16px 20px !important;
            font-size: 1rem !important;
            font-weight: 600 !important;
            text-align: center !important;
            margin: -3px -3px 0 -3px !important;
            border-radius: 13px 13px 0 0 !important;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
          }

          /* 🔧 FIX: Aggiungi overlay trasparente per catturare i click */
          .settings-dropdown-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: ${isMobile ? 'rgba(0, 0, 0, 0.4)' : 'transparent'};
            z-index: ${2147483647 - 1};
            pointer-events: auto;
          }
        `}
      </style>
      
      {/* Overlay per catturare click esterni */}
      <div 
        className="settings-dropdown-overlay"
        onClick={onClose}
      />
      
      {/* Dropdown Portal */}
      <div
        ref={dropdownRef}
        style={dropdownStyle}
        className="settings-dropdown-portal"
        onClick={(e) => e.stopPropagation()} // Previeni la chiusura quando si clicca nel dropdown
      >
        {/* Header solo su mobile */}
        {isMobile && (
          <div className="portal-dropdown-header">
            ⚙️ Opzioni Rapportino
          </div>
        )}
        
        {/* Contenuto dropdown */}
        <div className="portal-dropdown-content">
          {React.Children.map(children, (child, index) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                ...child.props,
                className: `portal-dropdown-item ${child.props.className || ''}`,
                key: index
              });
            }
            return child;
          })}
        </div>
      </div>
    </>
  );

  // Renderizza nel body usando createPortal
  return createPortal(portalContent, document.body);
};

export default SettingsDropdownPortal;